export interface LeadTrackingUtmTouchDataDto {
  utm_id: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  utm_medium: string | null;
  utm_source: string | null;
  date: string;
}

export interface LeadTrackingUtmDataDto {
  first_touch: LeadTrackingUtmTouchDataDto;
  last_touch?: LeadTrackingUtmTouchDataDto;
}

export class UtmTrackingService {
  private static readonly UTM_STORAGE_KEY = 'utm_data';

  private static readonly CRM_UTM_FIRST_COOKIE = 'crmutmfirst';

  private static readonly CRM_UTM_LAST_COOKIE = 'crmutmlast';

  private static readonly UTM_PARAMS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_campaign_id',
    'utm_campaign_name',
    'utm_term',
    'utm_content',
    'utm_id',
  ];

  /**
   * Extract UTM parameters from current URL
   */
  static extractUtmParams(): LeadTrackingUtmTouchDataDto {
    const urlParams = new URLSearchParams(window.location.search);
    const utmData: LeadTrackingUtmTouchDataDto = {
      utm_id: null,
      utm_campaign: null,
      utm_content: null,
      utm_term: null,
      utm_medium: null,
      utm_source: null,
      date: '',
    };

    // Extract standard UTM parameters
    utmData.utm_id = urlParams.get('utm_id');
    utmData.utm_source = urlParams.get('utm_source');
    utmData.utm_medium = urlParams.get('utm_medium');
    utmData.utm_campaign = urlParams.get('utm_campaign') || urlParams.get('utm_campaign_name');
    utmData.utm_term = urlParams.get('utm_term');
    utmData.utm_content = urlParams.get('utm_content');

    // Set default values for null parameters
    if (utmData.utm_campaign === null) utmData.utm_campaign = '(none)';
    if (utmData.utm_content === null) utmData.utm_content = '(none)';
    if (utmData.utm_term === null) utmData.utm_term = '(none)';

    // Set source to '(direct)' if no UTM parameters are present
    if (!utmData.utm_source) {
      utmData.utm_source = '(direct)';
    }

    // Set medium to '(none)' if no medium is specified
    if (!utmData.utm_medium) {
      utmData.utm_medium = '(none)';
    }

    const [date] = new Date().toISOString().split('T');
    utmData.date = date; // YYYY-MM-DD format

    return utmData;
  }

  /**
   * Get stored UTM data from localStorage
   */
  static getStoredUtmData(): LeadTrackingUtmDataDto | null {
    try {
      const stored = localStorage.getItem(this.UTM_STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error('Error parsing stored UTM data:', error);
      return null;
    }
  }

  /**
   * Store UTM data in localStorage
   */
  static storeUtmData(utmData: LeadTrackingUtmDataDto): void {
    try {
      localStorage.setItem(this.UTM_STORAGE_KEY, JSON.stringify(utmData));
    } catch (error) {
      console.error('Error storing UTM data:', error);
    }
  }

  /**
   * Update UTM data with current touch data
   */
  static updateUtmData(): LeadTrackingUtmDataDto {
    const currentUtmData = this.extractUtmParams();
    const storedData = this.getStoredUtmData();

    let firstTouch: LeadTrackingUtmTouchDataDto;
    let lastTouch: LeadTrackingUtmTouchDataDto;

    if (storedData) {
      // Use existing first touch, update last touch
      firstTouch = storedData.first_touch;
      lastTouch = currentUtmData;
    } else {
      // First visit - both first and last touch are the same
      firstTouch = currentUtmData;
      lastTouch = currentUtmData;
    }

    const utmData: LeadTrackingUtmDataDto = {
      first_touch: firstTouch,
      last_touch: lastTouch,
    };

    this.storeUtmData(utmData);
    return utmData;
  }

  /**
   * Get UTM data for API submission
   */
  static getUtmDataForApi(): LeadTrackingUtmDataDto | null {
    // First, try to get data from cookies (new method)
    const cookieData = this.getUtmDataFromCookies();
    if (cookieData) {
      return cookieData;
    }

    return null;

    // If there are UTM parameters in the current URL, update the data
    // if (this.hasUtmParams()) {
    //   return this.updateUtmData();
    // }
    //
    // // Otherwise, return stored data if available
    // return this.getStoredUtmData();
  }

  /**
   * Check if current URL has UTM parameters
   */
  static hasUtmParams(): boolean {
    const urlParams = new URLSearchParams(window.location.search);
    return this.UTM_PARAMS.some(param => urlParams.has(param));
  }

  /**
   * Clear stored UTM data
   */
  static clearUtmData(): void {
    localStorage.removeItem(this.UTM_STORAGE_KEY);
  }

  /**
   * Get cookie value by name
   */
  private static getCookie(name: string): string | null {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop()?.split(';').shift() || null;
    }
    return null;
  }

  /**
   * Decode URL-encoded JSON string from cookie
   */
  private static decodeUrlEncodedJson(encodedValue: string): any | null {
    try {
      if (!encodedValue) {
        return null;
      }

      // Decode URL-encoded string
      const decodedString = decodeURIComponent(encodedValue);

      // Parse JSON
      return JSON.parse(decodedString);
    } catch (error) {
      console.error('Failed to decode URL-encoded JSON:', error);
      return null;
    }
  }

  /**
   * Get UTM data from cookies (new method)
   */
  static getUtmDataFromCookies(): LeadTrackingUtmDataDto | null {
    const crmutmfirst = this.getCookie(this.CRM_UTM_FIRST_COOKIE);
    const crmutmlast = this.getCookie(this.CRM_UTM_LAST_COOKIE);

    if (!crmutmfirst) {
      return null;
    }

    const firstTouch = this.decodeUrlEncodedJson(crmutmfirst);

    if (!firstTouch) {
      return null;
    }

    const result: LeadTrackingUtmDataDto = {
      first_touch: firstTouch,
    };

    // Only add last_touch if crmutmlast cookie is present and valid
    if (crmutmlast) {
      const lastTouch = this.decodeUrlEncodedJson(crmutmlast);
      if (lastTouch) {
        result.last_touch = lastTouch;
      }
    }

    return result;
  }
}
